import { create } from 'zustand';

export type EditorTool =
  | 'select'
  | 'place'
  | 'move'
  | 'rotate'
  | 'scale'
  | 'wall_draw'
  | 'measure'
  | 'delete';

export type ViewMode = '2d' | '3d';

export interface EditorState {
  // Tool and view state
  activeTool: EditorTool;
  viewMode: ViewMode;
  isWalkthrough: boolean;
  /** 3D edit mode: camera frozen, objects can be added / moved / removed
   *  directly in the 3D view. Mutually exclusive with walkthrough. */
  is3DEditMode: boolean;
  isAnimating: boolean;
  /** When true, swingers render frozen at their max swing angle so a captured
   *  PNG / PDF makes it obvious the target moves. Set/cleared by the
   *  ExportDialog around capture. Independent of isAnimating. */
  showSwingersTilted: boolean;

  // Selection state
  selectedObjectIds: string[];
  hoveredObjectId: string | null;
  placingObjectType: string | null;
  placingObjectVariant: string | null;

  // Canvas state
  zoom: number;
  panOffset: { x: number; y: number };

  // Grid and ruler settings
  gridSize: number;
  snapToGrid: boolean;
  showGrid: boolean;
  showRuler: boolean;
  showMinimap: boolean;

  // UI state
  isDirty: boolean;
  isLeftPanelOpen: boolean;
  isRightPanelOpen: boolean;

  // Actions
  setTool: (tool: EditorTool) => void;
  setViewMode: (mode: ViewMode) => void;
  setWalkthrough: (active: boolean) => void;
  toggleWalkthrough: () => void;
  setEditMode3D: (active: boolean) => void;
  toggleEditMode3D: () => void;
  toggleAnimating: () => void;
  setShowSwingersTilted: (active: boolean) => void;
  selectObject: (id: string, multi?: boolean) => void;
  deselectAll: () => void;
  setHovered: (id: string | null) => void;
  setPlacing: (type: string | null, variant?: string | null) => void;
  setZoom: (zoom: number) => void;
  setPanOffset: (offset: { x: number; y: number }) => void;
  setGridSize: (size: number) => void;
  toggleSnapToGrid: () => void;
  toggleGrid: () => void;
  toggleRuler: () => void;
  toggleMinimap: () => void;
  setDirty: (dirty: boolean) => void;
  toggleLeftPanel: () => void;
  toggleRightPanel: () => void;
}

export const useEditorStore = create<EditorState>((set) => ({
  // Initial state
  activeTool: 'select',
  viewMode: '2d',
  isWalkthrough: false,
  is3DEditMode: false,
  isAnimating: false,
  showSwingersTilted: false,
  selectedObjectIds: [],
  hoveredObjectId: null,
  placingObjectType: null,
  placingObjectVariant: null,
  zoom: 1,
  panOffset: { x: 0, y: 0 },
  gridSize: 1,
  snapToGrid: true,
  showGrid: true,
  showRuler: true,
  showMinimap: false,
  isDirty: false,
  isLeftPanelOpen: true,
  isRightPanelOpen: true,

  // Actions
  setTool: (tool: EditorTool) => {
    // Clear any in-progress wall polyline when leaving the wall-draw tool
    if (tool !== 'wall_draw') {
      // Dynamic import so this store has no static dep on tools/
      void import('@/tools/wall-tool').then((m) => m.resetWallTool?.());
    }
    set({ activeTool: tool, placingObjectType: null, placingObjectVariant: null });
  },

  setViewMode: (mode: ViewMode) => {
    // Leaving 3D view always exits walkthrough and 3D edit mode.
    set((state) => ({
      viewMode: mode,
      isWalkthrough: mode === '3d' ? state.isWalkthrough : false,
      is3DEditMode: mode === '3d' ? state.is3DEditMode : false,
    }));
  },

  setWalkthrough: (active: boolean) => {
    set((state) => ({
      isWalkthrough: active && state.viewMode === '3d' ? true : false,
      // Walkthrough and 3D edit mode are mutually exclusive
      ...(active && state.viewMode === '3d' ? { is3DEditMode: false } : {}),
    }));
  },

  toggleWalkthrough: () => {
    set((state) => {
      const next = state.viewMode === '3d' ? !state.isWalkthrough : false;
      return { isWalkthrough: next, ...(next ? { is3DEditMode: false } : {}) };
    });
  },

  setEditMode3D: (active: boolean) => {
    set((state) => ({
      is3DEditMode: active && state.viewMode === '3d' ? true : false,
      ...(active && state.viewMode === '3d' ? { isWalkthrough: false } : {}),
    }));
  },

  toggleEditMode3D: () => {
    set((state) => {
      const next = state.viewMode === '3d' ? !state.is3DEditMode : false;
      return { is3DEditMode: next, ...(next ? { isWalkthrough: false } : {}) };
    });
  },

  toggleAnimating: () => {
    set((state) => ({ isAnimating: !state.isAnimating }));
  },

  setShowSwingersTilted: (active: boolean) => {
    set({ showSwingersTilted: active });
  },

  selectObject: (id: string, multi: boolean = false) => {
    set((state) => {
      if (multi) {
        return {
          selectedObjectIds: state.selectedObjectIds.includes(id)
            ? state.selectedObjectIds.filter((oid) => oid !== id)
            : [...state.selectedObjectIds, id],
        };
      }
      return { selectedObjectIds: [id] };
    });
  },

  deselectAll: () => {
    set({ selectedObjectIds: [] });
  },

  setHovered: (id: string | null) => {
    set({ hoveredObjectId: id });
  },

  setPlacing: (type: string | null, variant: string | null = null) => {
    if (type !== null) {
      set({
        activeTool: 'place',
        placingObjectType: type,
        placingObjectVariant: variant,
      });
    } else {
      set({
        placingObjectType: null,
        placingObjectVariant: null,
      });
    }
  },

  setZoom: (zoom: number) => {
    set({ zoom });
  },

  setPanOffset: (offset: { x: number; y: number }) => {
    set({ panOffset: offset });
  },

  setGridSize: (size: number) => {
    set({ gridSize: size });
  },

  toggleSnapToGrid: () => {
    set((state) => ({ snapToGrid: !state.snapToGrid }));
  },

  toggleGrid: () => {
    set((state) => ({ showGrid: !state.showGrid }));
  },

  toggleRuler: () => {
    set((state) => ({ showRuler: !state.showRuler }));
  },

  toggleMinimap: () => {
    set((state) => ({ showMinimap: !state.showMinimap }));
  },

  setDirty: (dirty: boolean) => {
    set({ isDirty: dirty });
  },

  toggleLeftPanel: () => {
    set((state) => ({ isLeftPanelOpen: !state.isLeftPanelOpen }));
  },

  toggleRightPanel: () => {
    set((state) => ({ isRightPanelOpen: !state.isRightPanelOpen }));
  },
}));
